import { useState, useEffect } from 'react';
import styles from './FilterPanel.module.scss';
import { H2, Spacer, Button, InputField } from '@components/index';
import { PanelState } from '../Table';
import CloseIcon from '@mui/icons-material/Close';
import { FilterOption } from './FilterOptions';
import { ColumnDef } from '@tanstack/react-table';
import {
	BUTTON_SIZE,
	BUTTON_TYPES,
	SPACER_DIRECTION,
	TTableFilterItem,
	TTableFilterDataObj
} from '@types';
import { FilterAltOutlined } from '@mui/icons-material';
type FilterPanelProps = {
    fromTableColumns: ColumnDef<any>[];
    saveFilters?: (f: TTableFilterDataObj) => void;
    applyFilter?: TTableFilterDataObj;
    clearAppliedFilter: (n:null) => void;
    deleteFilter: (id:string) => void;
    onApplyFilter?: (filter: TTableFilterDataObj) => void;
    openPanel?: (panelName: 'filter' | 'settings' | null) => void;
    closePanel?: () => void;
    panelState?: PanelState;
	onFilterDataChange?: (filter: TTableFilterDataObj) => void;
	hideButtonContainer?: boolean;
}

const FilterPanel = (props:FilterPanelProps) => {
	const {
		fromTableColumns,
		saveFilters,
		applyFilter = null,
		clearAppliedFilter,
		deleteFilter,
		onApplyFilter,
		closePanel,
		onFilterDataChange,
		hideButtonContainer = false
	} = props;
	const [filterData, setFilterData] = useState<TTableFilterDataObj>({});
	const [filterName, setFilterName] = useState('');
	const handleClosePanel = () => {
		closePanel?.();
	}
	const clearFilter = () => {
		setFilterData({});
		onFilterDataChange?.({})
		clearAppliedFilter(null);
	}
	const onChange = (key:string, value: TTableFilterItem | null) => {
		const newFilterData = { ...filterData };
		if (!value) {
			// null passed in so remove this filter
			delete newFilterData[key];

			if (Object.keys(newFilterData).length === 0) {
				// if this is the last filter, then clear the filters
				clearFilter();
			} else {
				setFilterData(newFilterData);
			}
		} else {
			newFilterData[key] = value;
			setFilterData(newFilterData);
		}
	}
	const saveFilter = () => {
		const clonedData = { ...filterData };
		if (filterData.filterName !== undefined && filterData.filterName !== filterName) {
			clonedData.filterName = filterName;
		}
		if (saveFilters) saveFilters(clonedData);
	}
	const isEmptyObj = (obj: TTableFilterDataObj) => {
		return Object.keys(obj).length === 0 && obj.constructor === Object;
	}
	useEffect(() => {
		if (applyFilter !== null) {
			setFilterData(applyFilter);
			onFilterDataChange?.(applyFilter)
		} else {
			setFilterData({});
			onFilterDataChange?.({});
		}
	}, [applyFilter]);
	useEffect(() => {
		setFilterName(filterData.filterName as string);
	}, [filterData]);
	return (
		<div className={styles.container}>
			<div className={styles.padded}>
				{
					closePanel &&
					<button className={styles.closeBtn} onClick={handleClosePanel} aria-label={'Close Filter Panel'}><CloseIcon /></button>
				}
				<span className={styles.title}>
					<FilterAltOutlined />
					<H2 text={'Filter'} />
				</span>
				<Spacer size={20} />
				{!isEmptyObj(filterData) && filterData.id && <>
					<InputField name={'Name of Filter'} autoComplete='off' label={'Name of Filter'} value={filterName} onChange={(e) => { setFilterName(e.target.value) }} />
					<Spacer size={20} />
				</>}
				{fromTableColumns.map((column: any, i:number) => {
					return <FilterOption
						key={i}
						columnId={column.accessorKey}
						filterData={filterData[column.accessorKey] || null}
						update={onChange}
						meta={column?.meta ?? {}}
						header={column?.header}
						canFilter={column?.meta?.canFilter ?? true}
					/>
				})}
			</div>
			{
				!hideButtonContainer &&
				<div className={styles.buttonContainer}>
					{!isEmptyObj(filterData) && <>
						<Button type={BUTTON_TYPES.TERTIARY} size={BUTTON_SIZE.SMALL} text={'Clear'} onClick={clearFilter} />
					</>}
					{!isEmptyObj(filterData) && filterData.id !== undefined && <>
						<Spacer size={20} dir={SPACER_DIRECTION.VERTICAL} />
						<Button size={BUTTON_SIZE.SMALL} text={'Delete Filter'} onClick={() => { deleteFilter(filterData.id as string) }} type={BUTTON_TYPES.SECONDARY} />
					</>}
					<Spacer size={10} dir={SPACER_DIRECTION.VERTICAL} />
					<Button size={BUTTON_SIZE.SMALL} type={BUTTON_TYPES.SECONDARY} text={filterData?.filterName ? 'Save & Apply' : 'Save'} onClick={saveFilter} disabled={isEmptyObj(filterData)} />
					{!isEmptyObj(filterData) && filterData.id === undefined && <>
						<Spacer size={10} dir={SPACER_DIRECTION.VERTICAL} />
						<Button size={BUTTON_SIZE.SMALL} text={'Apply'} onClick={() => { if (onApplyFilter) onApplyFilter(filterData) }} />
					</>}
				</div>
			}
		</div>
	);
}

export default FilterPanel;
